import React, { useContext, useState } from 'react';
import { useGetAllExteriorAssessmentSlots } from 'sellDirect/graphql/useGetAllExteriorAssessmentSlots';
import { useScheduleExteriorAssessment } from 'sellDirect/graphql/useScheduleExteriorAssessment';
import { useUpdateExteriorAssessment } from 'sellDirect/graphql/useUpdateExteriorAssessment';
import { useCancelExteriorAssessment } from 'sellDirect/graphql/useCancelExteriorAssessment';
import { LoadingPage } from 'platform/components/LoadingPage';
import { GenericErrorPage } from 'shared/routes/errors/GenericErrorPage';
import { Clock, LocalDate, LocalDateTime, LocalTime, ZoneId, ZonedDateTime } from '@js-joda/core';
import { useObservability } from 'shared/helpers/observability';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { useExperimentationContext } from 'sellDirect/ExperimentationContext';
import { TZ_FORMAT } from 'shared/helpers/datetime';

/**
 * IMPORTANT: Although the server stores EA / inspection slots as DateTimes,
 * this frontend only allows the user select the date, and we just hardcode the time to noon.
 * It's probably worth looking into whether this is the expected behavior or not.
 *
 * I suspect the reason it was implemented like this is the usage of DatePicker which only allows
 * dates to be picked without showing the time.
 */

export type LocalDateOrLocalDateTime = LocalDate | LocalDateTime | null;

export interface ISSEAContext {
  /** Currently saved date from server. */
  currentInspectionDate: LocalDateOrLocalDateTime;
  /** All EA slots. */
  availableExteriorAssessmentSlots: LocalDate[];
  availableExteriorAssessmentDateTimeSlots: LocalDateTime[];
  hasUpcomingEA: boolean;
  /** Currently selected date. Not saved to server yet. */
  selectedDate: LocalDateOrLocalDateTime;
  /** Sets currently selected date. */
  setSelectedDate: (selectedInspectionDate: LocalDate | LocalDateTime) => void;
  /** Whether `saveSelectedDate` can be called. */
  canSaveSelectedDate: boolean;
  /** Schedules/updates currently selected date to server. */
  saveSelectedDate: () => Promise<void>;
  /** Cancels EA. */
  cancelEA: () => Promise<void>;
  visitId?: string;
  earliestSlot: LocalDateOrLocalDateTime;
}

export const SSEAContext = React.createContext<ISSEAContext>({
  currentInspectionDate: null,
  availableExteriorAssessmentSlots: [],
  availableExteriorAssessmentDateTimeSlots: [],
  hasUpcomingEA: false,
  selectedDate: null,
  /** default to noop */
  setSelectedDate: () => {},
  canSaveSelectedDate: false,
  /** default to noop */
  saveSelectedDate: async () => {},
  /** default to noop */
  cancelEA: async () => {},
  visitId: undefined,
  earliestSlot: null,
});

/**
 * Hook to retrieve data and functions to for SSEA
 * @throws error if hook is used outside of the scope of SSEAContext
 * @returns SSEAContext data
 */
export const useSSEAContext = () => {
  const context = useContext<ISSEAContext>(SSEAContext);

  if (context === undefined) {
    throw new Error(`useSSEAContext must be used within a SSEAContext`);
  }
  return context;
};

/** TODO: Add documentation */
export const SSEAContextProvider: React.FC = ({ children }) => {
  const { trackEvent, trackError } = useObservability();
  const { sellerInputUUID, zoneId, refetchSellerLead } = useQualificationContext();
  const {
    assertions: { isInPersonSseaEnabled },
  } = useExperimentationContext();

  const {
    data: allExteriorAssessmentSlotsData,
    loading: allExteriorAssessmentSlotsLoading,
    error: allExteriorAssessmentSlotsError,
    refetch,
  } = useGetAllExteriorAssessmentSlots(sellerInputUUID, zoneId);

  const {
    scheduleExteriorAssessment,
    loading: useScheduleExteriorAssessmentLoading,
    error: useScheduleExteriorAssessmentError,
  } = useScheduleExteriorAssessment(sellerInputUUID);

  const {
    updateExteriorAssessment,
    loading: useUpdateExteriorAssessmentLoading,
    error: useUpdateExteriorAssessmentError,
  } = useUpdateExteriorAssessment(sellerInputUUID);

  const {
    cancelExteriorAssessment,
    loading: useCancelExteriorAssessmentLoading,
    error: useCancelExteriorAssessmentError,
  } = useCancelExteriorAssessment(sellerInputUUID);

  const visitId = allExteriorAssessmentSlotsData?.visitId;
  const currentInspectionDate = isInPersonSseaEnabled
    ? inspectionTimeToDateTime(allExteriorAssessmentSlotsData?.currentInspectionTime)
    : inspectionTimeToDate(allExteriorAssessmentSlotsData?.currentInspectionTime);
  const availableExteriorAssessmentSlots = getAvailableDays(
    allExteriorAssessmentSlotsData?.availableExteriorAssessmentSlots,
  );
  const availableExteriorAssessmentDateTimeSlots = getAvailableDateTimeSlots(
    allExteriorAssessmentSlotsData?.availableExteriorAssessmentSlots,
  );

  const earliestDateSlot = availableExteriorAssessmentSlots[0] ?? null;
  const earliestDateTimeSlot = availableExteriorAssessmentDateTimeSlots[0] ?? null;

  const earliestSlot: LocalDateOrLocalDateTime = isInPersonSseaEnabled
    ? earliestDateTimeSlot
    : earliestDateSlot;

  const [selectedDate, setSelectedDate] = useState<LocalDate | LocalDateTime | null>(null);

  /**
   * If the customer manually selected a date, use that.
   * Otherwise, if there is an inspection date use that (so that we can disable the Submit button until the customer selects a different time)
   * Finally, the only way the earliest slot is chosen is if:
   *    1. The customer has no current inspection date
   *    2. The customer has not selected any date manually
   */
  const dateToSave = selectedDate || currentInspectionDate || earliestSlot;
  const canSaveSelectedDate = !currentInspectionDate || !currentInspectionDate.equals(dateToSave);

  const saveSelectedDate = async () => {
    if (!dateToSave) {
      return;
    }

    let selectedTime;
    if (dateToSave instanceof LocalDate) {
      selectedTime = dateToSave.atTime(LocalTime.NOON).atZone(zoneId);
    }

    if (dateToSave instanceof LocalDateTime) {
      selectedTime = dateToSave.atZone(zoneId);
    }

    if (!selectedTime) {
      return;
    }

    if (visitId) {
      try {
        await updateExteriorAssessment(selectedTime, visitId);
        trackEvent('click', `reschedule-exterior-assessment`);
      } catch (e: CatchError) {
        trackError(`reschedule-exterior-assessment`, e);
      }
    } else {
      try {
        await scheduleExteriorAssessment(selectedTime);
        trackEvent('click', `schedule-exterior-assessment`);
      } catch (e: CatchError) {
        trackError(`schedule-exterior-assessment`, e);
      }
    }
    setSelectedDate(null);
    if (refetchSellerLead) {
      await refetchSellerLead();
    }
    await refetch();
  };

  const cancelEA = async () => {
    if (visitId) {
      try {
        await cancelExteriorAssessment(visitId);
        trackEvent('click', 'cancel-exterior-assessment');
      } catch (e: CatchError) {
        trackError('cancel-exterior-assessment', e);
      }
      if (refetchSellerLead) {
        await refetchSellerLead();
      }
      await refetch();
    }
  };

  const loading =
    allExteriorAssessmentSlotsLoading ||
    useScheduleExteriorAssessmentLoading ||
    useUpdateExteriorAssessmentLoading ||
    useCancelExteriorAssessmentLoading;

  if (loading) {
    return (
      <LoadingPage
        from="ExteriorAssessmentSchedule"
        reason={loading ? 'loading' : 'waitingOnExteriorAssessmentData'}
      />
    );
  }

  const error =
    allExteriorAssessmentSlotsError ||
    useScheduleExteriorAssessmentError ||
    useUpdateExteriorAssessmentError ||
    useCancelExteriorAssessmentError;

  if (error) {
    trackError('SSEAContext error', error);
    return <GenericErrorPage from="SSEAContext" />;
  }

  const hasUpcomingEA = checkHasUpcomingEA(currentInspectionDate, zoneId);

  return (
    <SSEAContext.Provider
      value={{
        currentInspectionDate,
        availableExteriorAssessmentSlots,
        availableExteriorAssessmentDateTimeSlots,
        selectedDate,
        setSelectedDate,
        canSaveSelectedDate,
        saveSelectedDate,
        cancelEA,
        visitId,
        earliestSlot,
        hasUpcomingEA,
      }}
    >
      {children}
    </SSEAContext.Provider>
  );
};

const getAvailableDays = (slots: ZonedDateTime[] | undefined): LocalDate[] => {
  if (!slots) {
    return [];
  }
  const daySlots: LocalDate[] = [];
  slots.forEach((slot) => {
    if (daySlots.every((daySlot) => !daySlot.equals(slot.toLocalDate()))) {
      daySlots.push(slot.toLocalDate());
    }
  });
  return daySlots;
};

const getAvailableDateTimeSlots = (slots: ZonedDateTime[] | undefined): LocalDateTime[] => {
  if (!slots) {
    return [];
  }
  return slots.map((s) => s.toLocalDateTime());
};

const inspectionTimeToDate = (
  currentInspectionTime: ZonedDateTime | null | undefined,
): LocalDate | null => {
  return currentInspectionTime?.toLocalDate() ?? null;
};

const inspectionTimeToDateTime = (
  currentInspectionTime: ZonedDateTime | null | undefined,
): LocalDateTime | null => {
  return currentInspectionTime?.toLocalDateTime() ?? null;
};

const checkHasUpcomingEA = (
  currentInspectionDate: LocalDateTime | LocalDate | null,
  zoneId: ZoneId | Clock | undefined,
): boolean => {
  if (currentInspectionDate instanceof LocalDate) {
    return !LocalDate.now(zoneId).isAfter(currentInspectionDate);
  }

  if (currentInspectionDate instanceof LocalDateTime) {
    return !LocalDateTime.now(zoneId).isAfter(currentInspectionDate);
  }

  return false;
};

/**
 * Text for morning and afternoon EA timeslots.
 * We don't have end times so morning is start time to 12PM
 * Afternoon is start time to 4PM
 * https://opendoor.slack.com/archives/C04LSD27Y66/p1676479174083899?thread_ts=1676407619.132529&cid=C04LSD27Y66
 */
export const formatInPersonEaMorningAfternoonTimeslot = (
  timeslotStartTime: LocalDateTime,
  zoneId?: ZoneId,
): string => {
  const abbreviatedZone = zoneId ? `${timeslotStartTime.atZone(zoneId).format(TZ_FORMAT)}` : '';

  if (timeslotStartTime.isBefore(timeslotStartTime.toLocalDate().atTime(LocalTime.NOON))) {
    return `8 AM to 12 PM${zoneId ? ' ' : ''}${abbreviatedZone}`;
  }

  return `12 PM to 4 PM${zoneId ? ' ' : ''}${abbreviatedZone}`;
};
